import { useCallback, useEffect, useState } from "react";
import {
  TrendingUp, Loader2, RefreshCw, FileDown, FileText, FileSpreadsheet,
  Award, Target, MapPin,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import "./CommunityIntelligence.css";

const WINDOWS = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "365d", days: 365 },
];

export default function FunderDashboard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/analytics/funder-dashboard?window_days=${days}`);
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const download = (format) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/funder-dashboard.${format}`
      + `?window_days=${days}&_t=${Date.now()}`;
    const token = localStorage.getItem("hw_access_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const filename = (r.headers.get("Content-Disposition") || "")
          .match(/filename="([^"]+)"/)?.[1] || `funder.${format}`;
        return r.blob().then((b) => ({ blob: b, filename }));
      })
      .then(({ blob, filename }) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
      })
      .catch((e) => setError(`Download failed: ${e.message || e}`));
  };

  return (
    <div className="hw-ci" data-testid="funder-dashboard">
      <div className="hw-ci__eyebrow">
        <Award className="w-3.5 h-3.5"
                style={{ display: "inline", verticalAlign: "text-bottom",
                          marginRight: "0.3rem" }} />
        Community Intelligence · Executive & Funder Dashboard
      </div>
      <h1 className="hw-ci__title">Impact at a glance</h1>
      <p className="hw-ci__sub">
        Cross-cutting community impact designed for board meetings, funder reports,
        and executive briefings. Everything below is exportable.
      </p>

      <div className="hw-ci__controls">
        {WINDOWS.map((w) => (
          <button key={w.days}
                   className={`hw-ci__btn ${days === w.days ? "hw-ci__btn--primary" : ""}`}
                   onClick={() => setDays(w.days)}
                   data-testid={`fd-window-${w.label}`}>
            {w.label}
          </button>
        ))}
        <button className="hw-ci__btn" onClick={load} data-testid="fd-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: "0.35rem" }}>
          <button className="hw-ci__btn" onClick={() => download("csv")}
                   data-testid="fd-export-csv">
            <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
          </button>
          <button className="hw-ci__btn hw-ci__btn--primary"
                   onClick={() => download("pdf")}
                   data-testid="fd-export-pdf">
            <FileText className="w-3.5 h-3.5" /> Impact Report (PDF)
          </button>
        </div>
      </div>

      {error && <div style={{ color: "#B91C1C" }}>{error}</div>}
      {loading && !data && (
        <div style={{ color: "#0F766E" }}>
          <Loader2 className="w-4 h-4 animate-spin"
                    style={{ display: "inline", verticalAlign: "text-bottom" }} /> loading…
        </div>
      )}

      {data && (
        <>
          <div className="hw-ci__kpi-grid" data-testid="fd-headline">
            <Kpi label="Care seekers served" value={data.headline.care_seekers_served} testid="fd-hd-cs" />
            <Kpi label="Referrals" value={data.headline.referrals_total} testid="fd-hd-referrals" />
            <Kpi label="Completed" value={data.headline.referrals_completed} testid="fd-hd-completed" />
            <Kpi label="Completion rate" value={`${data.headline.completion_rate_pct}%`} testid="fd-hd-rate" />
            <Kpi label="Active resources" value={data.headline.resources_active} testid="fd-hd-resources" />
            <Kpi label="Active volunteers" value={data.headline.volunteers_active} testid="fd-hd-volunteers" />
            <Kpi label="Avg wait (days)"
                  value={data.headline.avg_wait_days != null ? data.headline.avg_wait_days : "—"}
                  testid="fd-hd-wait" />
            <Kpi label="Resource deserts"
                  value={data.headline.deserts_flagged}
                  testid="fd-hd-deserts" />
          </div>

          <div className="hw-ci__panel" data-testid="fd-trends">
            <h2>
              <span>Trend series — last {days}d</span>
            </h2>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.trends.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" tick={{ fontSize: 10 }}
                        tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748B" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line dataKey="referrals" stroke="#0F766E" strokeWidth={2}
                       dot={false} name="Referrals" />
                <Line dataKey="completed" stroke="#22C55E" strokeWidth={2}
                       dot={false} name="Completed" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div className="hw-ci__panel" data-testid="fd-top-counties">
              <h2>
                <MapPin className="w-4 h-4"
                          style={{ display: "inline", verticalAlign: "text-bottom",
                                    marginRight: "0.3rem" }} />
                Top counties
              </h2>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.top_counties.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="key" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="referrals" fill="#0F766E" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="hw-ci__panel" data-testid="fd-program-roi">
              <h2>
                <Target className="w-4 h-4"
                         style={{ display: "inline", verticalAlign: "text-bottom",
                                   marginRight: "0.3rem" }} />
                Program impact (ROI proxy)
              </h2>
              <table className="hw-ci__table">
                <thead>
                  <tr>
                    <th>Program</th><th>Ref</th><th>Done</th><th>%</th><th>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {data.program_roi.slice(0, 10).map((p) => (
                    <tr key={p.program}>
                      <td><strong>{p.program}</strong></td>
                      <td>{p.referrals}</td>
                      <td>{p.completed}</td>
                      <td>{p.completion_rate_pct}%</td>
                      <td>{p.impact_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {data.resource_desert_top.length > 0 && (
            <div className="hw-ci__panel" data-testid="fd-deserts"
                  style={{ borderColor: "#F59E0B", borderWidth: "2px" }}>
              <h2>
                <span>Resource desert hotspots requiring investment</span>
                <small>Ideal for funder outreach</small>
              </h2>
              <table className="hw-ci__table">
                <thead>
                  <tr>
                    <th>County</th><th>Score</th><th>Severity</th>
                    <th>Referrals</th><th>Resources</th><th>Missing</th>
                  </tr>
                </thead>
                <tbody>
                  {data.resource_desert_top.map((h) => (
                    <tr key={h.key}>
                      <td><strong>{h.key}</strong></td>
                      <td style={{ fontWeight: 700 }}>{h.score}</td>
                      <td><span className={`hw-ci__pill hw-ci__pill--${h.severity}`}>
                        {h.severity}
                      </span></td>
                      <td>{h.referrals}</td>
                      <td>{h.active_resources}</td>
                      <td style={{ fontSize: "0.75rem", color: "#64748B" }}>
                        {h.categories_missing.slice(0, 3).join(", ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="hw-ci__kpi" data-testid={testid}>
      <div className="hw-ci__kpi-label">{label}</div>
      <div className="hw-ci__kpi-value">
        {typeof value === "number" ? new Intl.NumberFormat().format(value) : value}
      </div>
    </div>
  );
}
